// @flow

import { LAYOUTS } from '../video-layout/constants';

declare var interfaceConfig: Object;

/**
 * Checks whether there is any "priority content" active that should override
 * the recorder's default tile view.
 *
 * Priority content includes:
 * - Screen sharing (remote or local)
 * - Iframe sharing (whiteboard / etherpad)
 * - Moderator pin (pin for everyone)
 * - Shared video (YouTube tile)
 *
 * @param {Object} state - The Redux state.
 * @returns {boolean} True if any priority content is active.
 */
export function hasActivePriorityContent(state) {
    const participants = state['features/base/participants'];

    // Check for screen sharing (fake participants of type LocalScreenShare or RemoteScreenShare)
    const hasScreenShare = participants?.some(
        p => p.isFakeParticipant === 'LocalScreenShare'
            || p.isFakeParticipant === 'RemoteScreenShare'
    ) ?? false;

    // Check for iframe sharing (whiteboard or etherpad)
    const hasIframeSharing
        = state['features/genericiframe']?.visible
        || state['features/etherpad']?.editing
        || false;

    // Check for moderator pin (pin for everyone)
    const hasModeratorPin = participants?.some(p => p.moderatorPin) ?? false;

    // Check for shared video (YouTube tile)
    const sharedVideoState = state['features/shared-video'];
    const hasSharedVideo = Boolean(sharedVideoState?.ownerId || sharedVideoState?.videoUrl || sharedVideoState?.status === 'playing' || sharedVideoState?.status === 'start')
        || (participants?.some(p => p.fakeParticipant === 'SharedVideo' || p.isFakeParticipant === 'SharedVideo') ?? false);

    return hasScreenShare || hasIframeSharing || hasModeratorPin || hasSharedVideo;
}

/**
 * Determines the correct filmstrip view layout the recorder should be in,
 * taking into account:
 *   1. Whether any priority content is active (screen share, whiteboard,
 *      moderator pin, YouTube tile).
 *   2. The `set_recording_tile_view` pre-registration setting.
 *   3. The `hideParticipantTilesOnRecording` interface config flag.
 *
 * Decision table:
 * ┌──────────────────────────┬──────────────────────────────┬───────────────────┐
 * │ Priority content active? │ hideParticipantTilesOnRec    │ Target Layout     │
 * ├──────────────────────────┼──────────────────────────────┼───────────────────┤
 * │ YES                      │ true                         │ STAGE_VIEW        │
 * │ YES                      │ false                        │ VERTICAL_FILMSTRIP│
 * │ NO                       │ (set_recording_tile_view)    │ TILE_VIEW         │
 * │ NO                       │ hideParticipantTilesOnRec    │ STAGE_VIEW        │
 * │ NO                       │ otherwise                    │ VERTICAL_FILMSTRIP│
 * └──────────────────────────┴──────────────────────────────┴───────────────────┘
 *
 * @param {Object} state - The Redux state.
 * @returns {string} One of LAYOUTS.TILE_VIEW, LAYOUTS.STAGE_VIEW, or
 *   LAYOUTS.VERTICAL_FILMSTRIP_VIEW.
 */
export function getRecorderTargetView(state) {
    const { interfaceConfig: ifConfig } = state['features/base/config'];
    const pregData = state['features/base/mtApi']?.pregData;
    const hideParticipantTiles = ifConfig?.hideParticipantTilesOnRecording
        || (typeof interfaceConfig !== 'undefined' && interfaceConfig?.hideParticipantTilesOnRecording);
    const setRecordingTileView = pregData?.set_recording_tile_view;

    const hasPriority = hasActivePriorityContent(state);

    if (hasPriority) {
        // Priority content is active — never tile view
        return hideParticipantTiles
            ? LAYOUTS.STAGE_VIEW
            : LAYOUTS.VERTICAL_FILMSTRIP_VIEW;
    }

    // No priority content — use default recorder view
    if (setRecordingTileView) {
        return LAYOUTS.TILE_VIEW;
    }

    if (hideParticipantTiles) {
        return LAYOUTS.STAGE_VIEW;
    }

    return LAYOUTS.VERTICAL_FILMSTRIP_VIEW;
}
